import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const Header = ({ scrollToSection }) => {
  const { language, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t(language, 'nav.about'), id: 'about' },
    { label: t(language, 'nav.services'), id: 'services' },
    { label: t(language, 'nav.products'), id: 'products' },
    { label: t(language, 'nav.projects'), id: 'projects' },
    { label: t(language, 'nav.contact'), id: 'contact' },
  ];

  const handleNavClick = (id) => {
    scrollToSection(id);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_gas-solutions-2/artifacts/8j44ql1b_Screenshot%202025-12-23%20114917.png" 
                alt="NOBLE Gases & Solutions" 
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`font-medium transition-colors duration-300 hover:text-emerald-600 ${
                    isScrolled ? 'text-slate-700' : 'text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <Button 
                onClick={toggleLanguage}
                variant="ghost"
                size="sm"
                className={`flex items-center gap-2 ${isScrolled ? 'text-slate-700' : 'text-white'}`}
              >
                <Globe className="h-4 w-4" />
                {language === 'en' ? 'СР' : 'EN'}
              </Button>
              <Button 
                onClick={() => handleNavClick('contact')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-300 hover:scale-105"
              >
                {t(language, 'nav.getQuote')}
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className={`h-6 w-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
              ) : (
                <Menu className={`h-6 w-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900 md:hidden" style={{ top: '72px' }}>
          <nav className="flex flex-col items-center gap-6 py-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className="text-white text-xl font-medium hover:text-emerald-400 transition-colors"
              >
                {item.label}
              </button>
            ))}
            <Button 
              onClick={toggleLanguage}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-slate-900 flex items-center gap-2"
            >
              <Globe className="h-4 w-4" />
              {language === 'en' ? 'Српски' : 'English'}
            </Button>
            <Button 
              onClick={() => handleNavClick('contact')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white mt-4"
            >
              {t(language, 'nav.getQuote')}
            </Button>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;